import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getArticlesByCategory, getHotArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const articles = getArticlesByCategory(slug || 'all');
  const hotArticles = getHotArticles().slice(0, 5);

  const pageTitle = category ? `${category.name} - 51吃瓜网` : '分类 - 51吃瓜网';
  const pageDesc = category
    ? `51吃瓜网${category.name}频道，为您提供最新最全的${category.name}相关吃瓜内容。每日更新，不错过任何一个热门话题。`
    : '51吃瓜网分类页面，按不同主题浏览最新吃瓜内容。';

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${category?.name || '吃瓜'},51吃瓜网,吃瓜资讯,娱乐八卦`} />
      <link rel="canonical" href={`https://51chigua.com/category/${slug}`} />

      <main>
        <Navbar />

        {/* Header */}
        <section className="pt-20 pb-6 md:pb-8 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-3">
              {category?.name || '全部文章'}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {articles.length} 篇文章
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="flex-1">
                {/* Sub-category tabs */}
                <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200/70 hover:border-primary-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {articles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 text-foreground-500">
                    <p className="text-lg">该分类暂无文章</p>
                    <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block cursor-pointer">
                      返回首页
                    </Link>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="sticky top-24 space-y-6">
                  {/* Hot Sidebar */}
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-fire-line text-primary-500 text-lg"></i>
                      </span>
                      热门排行
                    </h3>
                    <div className="space-y-4">
                      {hotArticles.map((article, index) => (
                        <div key={article.id} className="flex items-start gap-3">
                          <span className={`text-xl font-bold flex-shrink-0 w-6 text-center ${
                            index < 3 ? 'text-primary-500' : 'text-foreground-400'
                          }`}>
                            {index + 1}
                          </span>
                          <Link
                            to={`/article/${article.slug}`}
                            className="flex-1 min-w-0 cursor-pointer"
                          >
                            <p className="text-sm text-foreground-950 line-clamp-2 leading-snug hover:text-primary-600 transition-colors">
                              {article.title}
                            </p>
                            <span className="text-xs text-foreground-400 mt-1 block">
                              {article.views.toLocaleString()} 阅读
                            </span>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tags Cloud */}
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4">
                      热门标签
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {['娱乐圈', '网红主播', '明星恋情', '社会热点', '直播翻车', '综艺', '搞笑', '正能量', '人设崩塌', '绯闻'].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}